import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Modal } from '@prio365/prio365-react-library';
import packageJson from '../../../package.json';
import { clearPrioCache, setPrioVersion } from '../../actions';
import history from '../../apps/main/history';
import { getCurrentPrioVersion } from '../../apps/main/rootReducer';
import Flex from '../../components/Flex';
import { makePrioStyles } from '../../theme/utils';
import { Task } from '@redux-saga/types';
import { useTheme } from 'react-jss';
import { PrioTheme } from '../../theme/types';
import { apiHelpGetReleases } from '../zendesk/api';
import { ReleaseDto } from '../../models/Prio365Features';
import classNames from 'classnames';
import { doVersionsDiffer } from '../../version/util';

const useStyles = makePrioStyles((theme) => ({
  root: {
    minWidth: '60%',
    margin: '20px auto',
    height: '96%',
    overflowY: 'hidden',
    '& > div:first-child': {
      height: '100%',
    },
    '& .prio-modal-section': {
      height: '100%',
    },
    '& .prio-modal-body': {
      height: 'calc(100% - 49px - 54px)',
      paddingTop: theme.spacing.extraSmall,
    },
  },
  rootWithoutCurrentRelease: {
    margin: '200px auto',
    '& > div:first-child': {
      height: 'unset',
    },
    '& .prio-modal-section': {
      height: 'unset',
    },
    '& .prio-modal-body': {
      height: 'unset',
      paddingTop: theme.spacing.extraSmall,
    },
  },
  fullWidth: {
    width: '100%',
    height: '100%',
  },
  icon: {
    color: `${theme.old.palette.chromaticPalette.yellow}!important`,
  },
  container: {
    padding: '1rem',
    borderRadius: '10px',
    boxShadow: '0 0 10px rgba(0,0,0,0.05)',
    '& ul': {
      listStyle: 'none',
      padding: 0,
      margin: 0,
    },
    '& li': {
      marginBottom: '1.2rem',
      backgroundColor: '#f1f6fc',
      padding: '1rem',
      borderLeft: '5px solid #0073e6',
      borderRadius: '5px',
    },
    '& h1': {
      color: '#0073e6',
      marginBottom: '0.5rem',
    },
    '& h2': {
      color: '#005bb5',
      marginTop: '2rem',
      borderBottom: '2px solid #eee',
      paddingBottom: '0.3rem',
    },
  },
  skeletonBg: {
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100vw',
    height: '100vh',
    zIndex: 1000,
    background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
    overflow: 'hidden',
  },
  dashboardLayout: {
    display: 'flex',
    height: '100vh',
    width: '100vw',
  },
  // Glass Sidebar
  glassSidebar: {
    width: '280px',
    background: 'rgba(255, 255, 255, 0.15)',
    backdropFilter: 'blur(20px)',
    WebkitBackdropFilter: 'blur(20px)',
    border: '1px solid rgba(255, 255, 255, 0.2)',
    borderLeft: 'none',
    borderTop: 'none',
    borderBottom: 'none',
    display: 'flex',
    flexDirection: 'column',
  },
  sidebarHeader: {
    padding: '20px',
    borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  logoSkeleton: {
    width: '32px',
    height: '32px',
    background:
      'linear-gradient(90deg, rgba(255, 255, 255, 0.2) 25%, rgba(255, 255, 255, 0.4) 50%, rgba(255, 255, 255, 0.2) 75%)',
    backgroundSize: '200% 100%',
    animation: '$shimmer 2s infinite ease-in-out',
    borderRadius: '8px',
  },
  titleSkeleton: {
    width: '120px',
    height: '20px',
    background:
      'linear-gradient(90deg, rgba(255, 255, 255, 0.2) 25%, rgba(255, 255, 255, 0.4) 50%, rgba(255, 255, 255, 0.2) 75%)',
    backgroundSize: '200% 100%',
    animation: '$shimmer 2s infinite ease-in-out',
    borderRadius: '4px',
  },
  sidebarNav: {
    flex: 1,
    padding: '20px 0',
  },
  navItemSkeleton: {
    padding: '12px 20px',
    margin: '2px 12px 8px 12px',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  navIconSkeleton: {
    width: '16px',
    height: '16px',
    background:
      'linear-gradient(90deg, rgba(255, 255, 255, 0.1) 25%, rgba(255, 255, 255, 0.3) 50%, rgba(255, 255, 255, 0.1) 75%)',
    backgroundSize: '200% 100%',
    animation: '$shimmer 2s infinite ease-in-out',
    borderRadius: '2px',
  },
  navTextSkeleton: {
    height: '14px',
    background:
      'linear-gradient(90deg, rgba(255, 255, 255, 0.1) 25%, rgba(255, 255, 255, 0.3) 50%, rgba(255, 255, 255, 0.1) 75%)',
    backgroundSize: '200% 100%',
    animation: '$shimmer 2s infinite ease-in-out',
    borderRadius: '2px',
    '&.short': { width: '80px' },
    '&.medium': { width: '120px' },
    '&.long': { width: '160px' },
  },
  projectSection: {
    padding: '20px',
    borderTop: '1px solid rgba(255, 255, 255, 0.1)',
  },
  projectTitleSkeleton: {
    width: '100px',
    height: '14px',
    background:
      'linear-gradient(90deg, rgba(255, 255, 255, 0.2) 25%, rgba(255, 255, 255, 0.4) 50%, rgba(255, 255, 255, 0.2) 75%)',
    backgroundSize: '200% 100%',
    animation: '$shimmer 2s infinite ease-in-out',
    borderRadius: '2px',
    marginBottom: '12px',
  },
  projectItemSkeleton: {
    padding: '8px 0',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  projectDotSkeleton: {
    width: '6px',
    height: '6px',
    background: 'rgba(255, 255, 255, 0.3)',
    borderRadius: '50%',
  },
  projectNameSkeleton: {
    height: '12px',
    background:
      'linear-gradient(90deg, rgba(255, 255, 255, 0.1) 25%, rgba(255, 255, 255, 0.3) 50%, rgba(255, 255, 255, 0.1) 75%)',
    backgroundSize: '200% 100%',
    animation: '$shimmer 2s infinite ease-in-out',
    borderRadius: '2px',
    '&.short': { width: '100px' },
    '&.long': { width: '140px' },
  },
  // Glass Main Content
  glassMainContent: {
    flex: 1,
    background: 'rgba(255, 255, 255, 0.05)',
    backdropFilter: 'blur(10px)',
    WebkitBackdropFilter: 'blur(10px)',
    padding: '24px',
    overflow: 'auto',
  },
  // Header
  glassHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '32px',
    padding: '16px 0',
  },
  headerTitleSkeleton: {
    width: '300px',
    height: '28px',
    background:
      'linear-gradient(90deg, rgba(255, 255, 255, 0.2) 25%, rgba(255, 255, 255, 0.4) 50%, rgba(255, 255, 255, 0.2) 75%)',
    backgroundSize: '200% 100%',
    animation: '$shimmer 2s infinite ease-in-out',
    borderRadius: '6px',
  },
  headerButtonSkeleton: {
    width: '150px',
    height: '36px',
    background: 'rgba(255, 255, 255, 0.15)',
    backdropFilter: 'blur(10px)',
    WebkitBackdropFilter: 'blur(10px)',
    border: '1px solid rgba(255, 255, 255, 0.2)',
    borderRadius: '8px',
  },
  // Dashboard Grid
  dashboardGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
    gap: '24px',
  },
  glassCard: {
    background: 'rgba(255, 255, 255, 0.15)',
    backdropFilter: 'blur(20px)',
    WebkitBackdropFilter: 'blur(20px)',
    border: '1px solid rgba(255, 255, 255, 0.2)',
    borderRadius: '16px',
    padding: '24px',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
    minHeight: '200px',
  },
  cardTitleSkeleton: {
    width: '180px',
    height: '18px',
    background:
      'linear-gradient(90deg, rgba(255, 255, 255, 0.2) 25%, rgba(255, 255, 255, 0.4) 50%, rgba(255, 255, 255, 0.2) 75%)',
    backgroundSize: '200% 100%',
    animation: '$shimmer 2s infinite ease-in-out',
    borderRadius: '4px',
    marginBottom: '20px',
  },
  // Circle Chart Skeleton
  circleChartSkeleton: {
    width: '120px',
    height: '120px',
    background: 'rgba(255, 255, 255, 0.1)',
    border: '8px solid rgba(255, 255, 255, 0.2)',
    borderTop: '8px solid rgba(255, 255, 255, 0.4)',
    borderRadius: '50%',
    margin: '0 auto 20px auto',
    animation: '$spin 3s linear infinite',
  },
  // Stats Grid Skeleton
  statsGridSkeleton: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(80px, 1fr))',
    gap: '12px',
    marginTop: '20px',
  },
  statItemSkeleton: {
    padding: '16px',
    background: 'rgba(255, 255, 255, 0.1)',
    borderRadius: '12px',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    textAlign: 'center',
  },
  statNumberSkeleton: {
    width: '40px',
    height: '24px',
    background:
      'linear-gradient(90deg, rgba(255, 255, 255, 0.2) 25%, rgba(255, 255, 255, 0.4) 50%, rgba(255, 255, 255, 0.2) 75%)',
    backgroundSize: '200% 100%',
    animation: '$shimmer 2s infinite ease-in-out',
    borderRadius: '4px',
    margin: '0 auto 8px auto',
  },
  statLabelSkeleton: {
    width: '60px',
    height: '12px',
    background:
      'linear-gradient(90deg, rgba(255, 255, 255, 0.1) 25%, rgba(255, 255, 255, 0.3) 50%, rgba(255, 255, 255, 0.1) 75%)',
    backgroundSize: '200% 100%',
    animation: '$shimmer 2s infinite ease-in-out',
    borderRadius: '2px',
    margin: '0 auto',
  },
  // List Skeleton
  listSkeleton: {
    '& > div': {
      padding: '12px 0',
      borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      '&:last-child': {
        borderBottom: 'none',
      },
    },
  },
  listItemSkeleton: {
    height: '14px',
    background:
      'linear-gradient(90deg, rgba(255, 255, 255, 0.1) 25%, rgba(255, 255, 255, 0.3) 50%, rgba(255, 255, 255, 0.1) 75%)',
    backgroundSize: '200% 100%',
    animation: '$shimmer 2s infinite ease-in-out',
    borderRadius: '2px',
    '&.short': { width: '120px' },
    '&.medium': { width: '160px' },
    '&.long': { width: '200px' },
  },
  listBadgeSkeleton: {
    width: '20px',
    height: '20px',
    background: 'rgba(255, 255, 255, 0.2)',
    borderRadius: '50%',
  },
  // Content Area Skeleton
  contentAreaSkeleton: {
    padding: '40px 0',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '16px',
  },
  contentLineSkeleton: {
    height: '16px',
    background:
      'linear-gradient(90deg, rgba(255, 255, 255, 0.1) 25%, rgba(255, 255, 255, 0.3) 50%, rgba(255, 255, 255, 0.1) 75%)',
    backgroundSize: '200% 100%',
    animation: '$shimmer 2s infinite ease-in-out',
    borderRadius: '2px',
    '&.full': { width: '100%' },
    '&.three-quarter': { width: '75%' },
    '&.half': { width: '50%' },
  },
  // Animations
  '@keyframes shimmer': {
    '0%': {
      backgroundPosition: '-200% 0',
    },
    '100%': {
      backgroundPosition: '200% 0',
    },
  },
  '@keyframes spin': {
    '0%': {
      transform: 'rotate(0deg)',
    },
    '100%': {
      transform: 'rotate(360deg)',
    },
  },
}));

interface NewPrioVersionModalProps {
  task: Task;
}

export const NewPrioVersionModal: React.FC<NewPrioVersionModalProps> = (
  props
) => {
  //#region ------------------------------ Defaults
  const classes = useStyles();
  const theme = useTheme<PrioTheme>();
  const { task } = props;
  const { t } = useTranslation();
  const dispatch = useDispatch();
  //#endregion

  //#region ------------------------------ States / Attributes / Selectors
  const videoPlayerRef = useRef<HTMLDivElement>(null);

  const [currentRelease, setCurrentRelease] = useState<ReleaseDto | null>(null);
  const [currentReleaseUrl, setCurrentReleaseUrl] = useState<string | null>(
    null
  );
  const [videoPlayHeight, setVideoPlayerHeight] = useState<number>(0);

  const prioVersion = useSelector(getCurrentPrioVersion);
  //#endregion

  //#region ------------------------------ Methods / Handlers
  const handleClearReload = async () => {
    dispatch(clearPrioCache());
    dispatch(setPrioVersion(packageJson.version));
    setTimeout(() => {
      history.replace('/module');
      window.location.reload();
    }, 2500);
  };

  //#endregion

  //#region ------------------------------ Effects
  useEffect(() => {
    var version = packageJson.version.split('-')[0];
    try {
      const fetchReleases = async () => {
        try {
          const { data, result } = await apiHelpGetReleases();
          if (result.status >= 200 && result.status < 300 && data) {
            const current = data.find((release) => release.version === version);
            setCurrentRelease(current || null);
            var releaseUrl = current?.releaseUrl || '';
            if (releaseUrl) {
              const currentReleaseUrl = releaseUrl.includes('/')
                ? releaseUrl.split('/').pop()
                : releaseUrl;
              setCurrentReleaseUrl(currentReleaseUrl);
            }
          } else {
          }
        } catch (err) {
          // nothing to do here
        }
      };

      fetchReleases();
    } catch (error) {}
  }, [prioVersion]);

  useEffect(() => {
    if (doVersionsDiffer(prioVersion)) {
      task.cancel();
    } else {
      dispatch(setPrioVersion(packageJson.version));
    }
  }, [prioVersion, dispatch, task]);

  useLayoutEffect(() => {
    if (!videoPlayerRef.current) return undefined;

    const update = () => {
      const h = videoPlayerRef.current?.offsetHeight || 0;
      setVideoPlayerHeight(h);
    };

    update(); // initial

    const ro = new ResizeObserver(update);
    ro.observe(videoPlayerRef.current);

    window.addEventListener('resize', update); // Fallback

    return () => {
      ro.disconnect();
      window.removeEventListener('resize', update);
    };
  }, [currentReleaseUrl]);

  //#endregion

  return doVersionsDiffer(prioVersion) ? (
    <div className={classes.skeletonBg}>
      <div className={classes.dashboardLayout}>
        {/* Glass Sidebar Skeleton */}
        <div className={classes.glassSidebar}>
          <div className={classes.sidebarHeader}>
            <div className={classes.logoSkeleton}></div>
            <div className={classes.titleSkeleton}></div>
          </div>

          <nav className={classes.sidebarNav}>
            {[...Array(8)].map((_, i) => (
              <div key={i} className={classes.navItemSkeleton}>
                <div className={classes.navIconSkeleton}></div>
                <div
                  className={`${classes.navTextSkeleton} ${i % 3 === 0 ? 'short' : i % 3 === 1 ? 'medium' : 'long'}`}
                ></div>
              </div>
            ))}
          </nav>

          <div className={classes.projectSection}>
            <div className={classes.projectTitleSkeleton}></div>
            {[...Array(4)].map((_, i) => (
              <div key={i} className={classes.projectItemSkeleton}>
                <div className={classes.projectDotSkeleton}></div>
                <div
                  className={`${classes.projectNameSkeleton} ${i % 2 === 0 ? 'short' : 'long'}`}
                ></div>
              </div>
            ))}
          </div>
        </div>

        {/* Glass Main Content Skeleton */}
        <div className={classes.glassMainContent}>
          <div className={classes.glassHeader}>
            <div className={classes.headerTitleSkeleton}></div>
            <div className={classes.headerButtonSkeleton}></div>
          </div>

          <div className={classes.dashboardGrid}>
            {/* Card with Circle Chart */}
            <div className={classes.glassCard}>
              <div className={classes.cardTitleSkeleton}></div>
              <div className={classes.circleChartSkeleton}></div>
            </div>

            {/* Card with Stats Grid */}
            <div className={classes.glassCard}>
              <div className={classes.cardTitleSkeleton}></div>
              <div className={classes.statsGridSkeleton}>
                {[...Array(3)].map((_, i) => (
                  <div key={i} className={classes.statItemSkeleton}>
                    <div className={classes.statNumberSkeleton}></div>
                    <div className={classes.statLabelSkeleton}></div>
                  </div>
                ))}
              </div>
            </div>

            {/* Card with Stats Grid - 4 items */}
            <div className={classes.glassCard}>
              <div className={classes.cardTitleSkeleton}></div>
              <div className={classes.statsGridSkeleton}>
                {[...Array(4)].map((_, i) => (
                  <div key={i} className={classes.statItemSkeleton}>
                    <div className={classes.statNumberSkeleton}></div>
                    <div className={classes.statLabelSkeleton}></div>
                  </div>
                ))}
              </div>
            </div>

            {/* Card with Content Area */}
            <div className={classes.glassCard}>
              <div className={classes.cardTitleSkeleton}></div>
              <div className={classes.contentAreaSkeleton}>
                <div className={`${classes.contentLineSkeleton} full`}></div>
                <div
                  className={`${classes.contentLineSkeleton} three-quarter`}
                ></div>
              </div>
            </div>

            {/* Card with Content Area - Simple */}
            <div className={classes.glassCard}>
              <div className={classes.cardTitleSkeleton}></div>
              <div className={classes.contentAreaSkeleton}>
                <div className={`${classes.contentLineSkeleton} half`}></div>
              </div>
            </div>

            {/* Card with List */}
            <div className={classes.glassCard}>
              <div className={classes.cardTitleSkeleton}></div>
              <div className={classes.listSkeleton}>
                {[...Array(4)].map((_, i) => (
                  <div key={i}>
                    <div
                      className={`${classes.listItemSkeleton} ${i % 2 === 0 ? 'medium' : 'long'}`}
                    ></div>
                    <div className={classes.listBadgeSkeleton}></div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Your existing Modal */}
      <Modal
        className={classNames(classes.root, {
          [classes.rootWithoutCurrentRelease]: !currentRelease,
        })}
        visible
        title={t('common:newPrioVersionModal.title')}
        maskClosable={false}
        keyboard={false}
        closable={false}
        footer={
          <Button
            id="prio-new-version-modal-refresh"
            onClick={() => handleClearReload()}
          >
            {t('common:newPrioVersionModal.confirm')}
          </Button>
        }
      >
        <Flex.Column
          id="prio-new-version-modal"
          childrenGap={theme.old.spacing.unit(2)}
          height={'100%'}
        >
          <Flex.Item className={classes.fullWidth}>
            {currentReleaseUrl && (
              <div
                ref={videoPlayerRef}
                style={{ padding: '51.17% 0 0 0', position: 'relative' }}
              >
                <iframe
                  src={`https://player.vimeo.com/video/${currentReleaseUrl}?badge=0&amp;autopause=0&amp;player_id=0&amp;app_id=58479`}
                  allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share"
                  style={{
                    border: 'none',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                  }}
                  title={currentRelease.version}
                  onLoad={() => {
                    if (videoPlayerRef.current) {
                      setVideoPlayerHeight(videoPlayerRef.current.offsetHeight);
                    }
                  }}
                ></iframe>
              </div>
            )}
            {currentRelease?.textGerman ? (
              <Flex.Row
                justifyContent="space-between"
                height={`calc(100% - ${videoPlayHeight}px)`}
                overflowY={'auto'}
                marginTop={theme.spacing.extraSmall}
              >
                <Flex.Item className={classes.container}>
                  <div
                    dangerouslySetInnerHTML={{
                      __html: currentRelease.textGerman,
                    }}
                  />
                </Flex.Item>
              </Flex.Row>
            ) : (
              <Flex.Row alignItems="center" justifyContent="space-between">
                <Flex.Item paddingTop="16px">
                  <p>{t('common:newPrioVersionModal.noDescription')}</p>
                </Flex.Item>
              </Flex.Row>
            )}
            <script src="https://player.vimeo.com/api/player.js"></script>
          </Flex.Item>
        </Flex.Column>
      </Modal>
    </div>
  ) : (
    <></>
  );
};

export default NewPrioVersionModal;
